import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { ThemeService } from '../../services/theme.service';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../services/auth';
import { SidebarService } from '../../services';
import { Avatar } from '../avatar/avatar';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, distinctUntilChanged, toArray } from 'rxjs/operators';
import { concat } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SystemAdminService } from '../../../services/system-admin/system-admin.service';
import { MembershipService } from '../../../services/membership/membership.service';
import { Modal } from '../modal/modal';
import { EventAdd } from '../event-add/event-add';
import { ProductSalesModal } from '../../../features/agendas/product-sales-modal/product-sales-modal';
import { AgendaService } from '../../../services/business';
import { ToastService } from '../../services';
import { EventStatusCode, STATUS_DEF_MAP } from '../../api/eventStatus.token';
import { CreateAgendaEventDto } from '../../interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingWelcomeService } from '../../services/onboarding-welcome.service';
import { mergeDateAndTime } from '../../common/utils';

@Component({
  selector: 'app-header',
  imports: [CommonModule, ClickOutsideDirective, RouterLink, Avatar, Modal, ProductSalesModal],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header {
  @Input() mobileSidebarOpen = false;
  @Output() sidebarToggle = new EventEmitter<void>();
  menuToggle: boolean = false;
  dropdownOpen: boolean = false;
  dropdownOpenNotification: boolean = false;
  notifying = true;
  router = inject(Router);
  authService = inject(AuthService);
  systemAdminService = inject(SystemAdminService);
  loggedUser = computed(() => this.authService.getUserInfo());
  businessName = computed(() => this.authService.getBusinessName());
  isOwner = computed(() => this.authService.isOwner());

  /** System admins outside a business see the admin panel, not a business */
  isAdminPanel = computed(
    () => this.authService.isSystemAdmin() && !this.authService.isImpersonating()
  );

  businessInitial = computed(() => {
    const name = (this.businessName() || '').trim();
    return name ? name[0].toUpperCase() : 'N';
  });

  isDark = computed(() => this.themeService.darkModeEnabled());

  private readonly dateFormatter = new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  /** Today's date, e.g. "viernes, 23 de octubre" */
  get today(): string {
    return this.dateFormatter.format(new Date());
  }
  private sidebarService = inject(SidebarService);
  private bp = inject(BreakpointObserver);

  // Quick actions
  quickActionsOpen = false;
  membershipService = inject(MembershipService);
  private agendaApi = inject(AgendaService);
  private toastService = inject(ToastService);
  private statusDefs = inject(STATUS_DEF_MAP);

  private onboardingService = inject(OnboardingService);
  private onboardingWelcomeService = inject(OnboardingWelcomeService);

  // Feature-based visibility for quick actions
  canCreateSale = signal(false);
  canCreateEvent = computed(() => !this.onboardingService.shouldShowChecklist());

  // Event creation (from FabButton logic)
  EventAddForm = EventAdd;
  modalBodyInputs: Record<string, unknown> = {};
  openEventForm = false;
  savingEvent = signal(false);

  // Product sales
  salesModalOpen = signal(false);

  // Track if we're on desktop (lg and up) to determine toggle behavior
  isLgUp = toSignal(
    this.bp.observe('(min-width: 1024px)').pipe(
      map((r) => r.matches),
      distinctUntilChanged(),
    ),
    {
      initialValue:
        typeof window !== 'undefined' &&
        matchMedia('(min-width: 1024px)').matches,
    },
  );

  constructor(private themeService: ThemeService) {
    this.loadQuickActionFeatures();
    this.onboardingService.checkPrerequisites();
  }

  private loadQuickActionFeatures(): void {
    // System admins bypass feature checks
    if (this.authService.isSystemAdmin()) {
      this.canCreateSale.set(true);
      return;
    }

    // Check sync first (from cache)
    const hasSalesSync = this.membershipService.hasFeature('Ventas y reportes integrados');
    if (hasSalesSync) {
      this.canCreateSale.set(true);
      return;
    }

    // Load async if not cached
    this.membershipService.hasFeatureAsync('Ventas y reportes integrados').subscribe(hasFeature => {
      this.canCreateSale.set(hasFeature);
    });
  }

  toggleSidebar() {
    // On desktop, toggle the sidebar collapse state (mini mode)
    // On mobile, emit event to parent layout to toggle mobile sidebar
    if (this.isLgUp()) {
      this.sidebarService.toggleSidebar();
    } else {
      this.sidebarToggle.emit();
    }
  }

  toggleMenu(event: Event) {
    event.stopPropagation();
    this.menuToggle = !this.menuToggle;
  }

  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
    this.quickActionsOpen = false;
  }

  @HostListener('document:keydown.escape')
  closeMenus(): void {
    this.dropdownOpen = false;
    this.quickActionsOpen = false;
  }

  toggleDropdownNotification() {
    this.dropdownOpenNotification = !this.dropdownOpenNotification;
  }

  signOut() {
    this.authService.logout();
    this.router.navigate(['/auth/signin']);
  }

  openWelcomeGuide() {
    this.dropdownOpen = false;
    this.onboardingWelcomeService.open();
  }

  routerProfile(url: string) {
    this.dropdownOpen = false;
    this.router.navigateByUrl(url);
  }

  // --- Quick Actions ---

  toggleQuickActions() {
    this.quickActionsOpen = !this.quickActionsOpen;
    this.dropdownOpen = false;
  }

  openCreateEvent() {
    this.quickActionsOpen = false;
    this.modalBodyInputs = { selectedDate: new Date() };
    this.openEventForm = true;
  }

  openRegisterSale() {
    this.quickActionsOpen = false;
    this.salesModalOpen.set(true);
  }

  onEventModalConfirm(payload: any): void {
    if (!payload) return;
    this.savingEvent.set(true);
    const code = 'completed' as EventStatusCode;

    if (payload.multiService && Array.isArray(payload.events)) {
      this.createMultiServiceEvents(payload.events, code);
      return;
    }

    const start = mergeDateAndTime(payload.startDate, payload.startTime);
    const end = mergeDateAndTime(payload.endDate, payload.endTime);
    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    const body: CreateAgendaEventDto = {
      ...payload,
      startDate: start,
      endDate: end,
      statusId: this.statusDefs[code]?.eventStatusId,
    };

    this.agendaApi.createAgendaEvent(body).subscribe({
      next: (res) => {
        if (res.eventId) {
          this.openEventForm = false;
          this.toastService.show(
            'Evento creado correctamente, puedes verlo en el calendario',
            'success',
            4000
          );
          this.savingEvent.set(false);
          this.agendaApi.notifyEventCreated();
        }
      },
      error: (err: HttpErrorResponse) => {
        const api = err.error as { code?: string; errors?: string[]; message?: string; details?: string };
        const msg =
          (api?.errors?.length ? api.errors.join('\n') : null) ||
          api?.details ||
          api?.message ||
          err.message ||
          'Ocurrio un error inesperado.';
        this.toastService.show(msg, 'warning', 6000);
        this.savingEvent.set(false);
      },
    });
  }

  private createMultiServiceEvents(events: any[], statusCode: EventStatusCode): void {
    const calls$ = events.map((ev: any) => {
      const start = mergeDateAndTime(ev.startDate, ev.startTime);
      const end = mergeDateAndTime(ev.endDate, ev.endTime);
      if (end <= start) end.setDate(end.getDate() + 1);

      return this.agendaApi.createAgendaEvent({
        ...ev,
        startDate: start,
        endDate: end,
        statusId: this.statusDefs[statusCode]?.eventStatusId,
      });
    });

    concat(...calls$).pipe(toArray()).subscribe({
      next: () => {
        this.openEventForm = false;
        this.toastService.show(
          `${events.length} servicios agendados correctamente`,
          'success',
          4000
        );
        this.savingEvent.set(false);
        this.agendaApi.notifyEventCreated();
      },
      error: (err: HttpErrorResponse) => {
        const api = err.error as { code?: string; errors?: string[]; message?: string; details?: string };
        const msg =
          (api?.errors?.length ? api.errors.join('\n') : null) ||
          api?.details ||
          api?.message ||
          err.message ||
          'Ocurrio un error inesperado.';
        this.toastService.show(msg, 'warning', 6000);
        this.savingEvent.set(false);
      },
    });
  }

  closeSalesModal() {
    this.salesModalOpen.set(false);
  }

  stopImpersonation() {
    this.systemAdminService.stopImpersonation().subscribe({
      next: (response) => {
        // Get current auth data from localStorage to preserve user info
        const stored = localStorage.getItem('auth_data');
        if (!stored) {
          console.error('No current auth data found');
          return;
        }

        const currentAuthData = JSON.parse(stored);

        // Create new AuthenticatedUser with updated token
        // Remove business-related fields when stopping impersonation
        const updatedAuthData = {
          ...currentAuthData,
          token: response.token,
          expiresAt: response.expiresAt,
        };

        // Remove business fields from the object
        delete updatedAuthData.business_name;
        delete updatedAuthData.business_id;
        delete updatedAuthData.business_is_active;

        // Update localStorage first
        localStorage.setItem('auth_data', JSON.stringify(updatedAuthData));

        // Update auth service with new token
        this.authService.login(updatedAuthData);

        // Navigate to system admin panel
        this.router.navigate(['/system-admin']);
      },
      error: (err) => {
        console.error('Error stopping impersonation:', err);
        alert('Error al detener impersonación');
      },
    });
  }
}
